import React, { useRef } from 'react';
import classNames from 'classnames';
import { useLocation } from 'react-router-dom';

import { useIntl } from '../../../../util/reactIntl';
import { parse, stringify } from '../../../../util/urlHelpers';
import { CATEGORY_LEVEL_1, getCategoryList } from '../../../../util/categories';
import { NamedLink } from '../../../../components';

import useHideOnScrollDown from './useHideOnScrollDown';

import css from './TopbarCategoryRail.module.css';

/**
 * The nine wedding service categories, as a persistent row under the search bar.
 *
 * Each item links to the search page with both category params applied, keeping
 * every other param already in the URL — a date or region set in the search bar
 * must survive a category click. Clicking the active category clears it, so the
 * rail is a toggle rather than a one-way trip.
 *
 * @component
 * @param {Object} props
 * @param {string?} props.className add more style rules in addition to components own css.root
 * @param {string?} props.rootClassName overwrite components own css.root
 * @param {React.Node?} props.children a row above the categories that retreats with them
 * @returns {JSX.Element} category rail
 */
const TopbarCategoryRail = props => {
  const { className, rootClassName, children } = props;
  const location = useLocation();
  const intl = useIntl();
  const railRef = useRef(null);
  const isHidden = useHideOnScrollDown(railRef);

  const currentParams = parse(location.search);
  const activeId = currentParams.pub_categoryLevel2;

  const searchForCategory = id => {
    const { pub_categoryLevel1, pub_categoryLevel2, ...rest } = currentParams;
    const isActive = pub_categoryLevel2 === id;
    const categoryParams = isActive
      ? {}
      : { pub_categoryLevel1: CATEGORY_LEVEL_1, pub_categoryLevel2: id };
    const query = stringify({ ...rest, ...categoryParams });
    return query ? `?${query}` : '';
  };

  return (
    // One retreating box for the categories and anything above them. useHideOnScrollDown takes
    // back the height its own element loses, so a second row collapsing on its own would read to
    // it as a scroll and flap both.
    <div
      ref={railRef}
      className={classNames(rootClassName || css.root, className, {
        [css.rootWithLeadingRow]: !!children,
        [css.hidden]: isHidden,
      })}
      {...(isHidden ? { inert: '' } : {})}
    >
      {children}
      <nav className={css.nav} aria-label={intl.formatMessage({ id: 'TopbarCategoryRail.label' })}>
        <ul className={css.list}>
          {getCategoryList().map(({ slug, id, label }) => {
            const isActive = activeId === id;
            return (
              <li key={slug} className={css.item}>
                <NamedLink
                  name="SearchPage"
                  to={{ search: searchForCategory(id) }}
                  className={classNames(css.link, { [css.linkActive]: isActive })}
                  {...(isActive ? { 'aria-current': 'page' } : {})}
                >
                  {label}
                </NamedLink>
              </li>
            );
          })}
        </ul>
      </nav>
    </div>
  );
};

export default TopbarCategoryRail;
